import React from "react";
import { Link } from "react-router-dom";
import { FiMessageCircle } from "react-icons/fi";
import parse from "html-react-parser";
import DOMPurify from "dompurify";
import ArticleDTO from "../../../../Models/DTOs/ArticleDTO";
import { ContentType } from "../../../../Models/Enums/ContentType";
import IconTime from "../../../../Assets/SVGs/Icons/TimeAndDate/Time";
import ImageLazyLoad from "../../../UI/ImageLazyLoad";
import { ArticleCategoryTerm } from "../../../Buttons/ArticleCategoryTerm";
import { SocialsMedia } from "../../../UI/SocialsMedia";

type Props = {
    article: ArticleDTO | null;
    ReadTime: number | undefined;
    DateToString: string | undefined;
    commentsRef?: React.RefObject<HTMLDivElement>;
};

const ArticleHeader = ({ article, DateToString, ReadTime, commentsRef }: Props) => {
    if (!article) return null;
    const authorName = article.authors?.[0]?.Name;
    const title = article.title || "Untitled article";

    return (
        <div className="article-header">
            <div className="article-header__inner">
                <div className="article-header__column">
                    {!!article.categories?.length && (
                        <div className="article-header__categories u-relative_hidden is-not-loading">
                            {article.categories.map((category) => <ArticleCategoryTerm category={category} key={category.id} />)}
                        </div>
                    )}
                    <h1 className="u-relative_hidden is-not-loading">{parse(DOMPurify.sanitize(title))}</h1>
                    {article.subTitle && (
                        <h3 className="u-relative_hidden is-not-loading">{parse(DOMPurify.sanitize(article.subTitle))}</h3>
                    )}
                </div>

                <div className="article-header__info">
                    {authorName && (
                        <>
                            <div className="article-header__info-text u-relative_hidden is-not-loading"><p>By: {authorName}</p></div>
                            <span className="article-header__dot">&bull;</span>
                        </>
                    )}
                    {DateToString && (
                        <>
                            <div className="article-header__info-text u-relative_hidden is-not-loading"><p>{DateToString}</p></div>
                            <span className="article-header__dot">&bull;</span>
                        </>
                    )}
                    <Link to="https://www.ickonic.com/" className="article-header__info-text article-header__info-link">
                        ickonic.com
                    </Link>
                    <span className="article-header__dot">&bull;</span>
                    {commentsRef && (
                        <button
                            type="button"
                            className="article-header__info-comments"
                            aria-controls="article-comments"
                            onClick={() => {
                                commentsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                                commentsRef.current?.focus({ preventScroll: true });
                            }}
                        >
                            <FiMessageCircle aria-hidden="true" />
                            Comments
                        </button>
                    )}
                    <SocialsMedia
                        isShare={false}
                        title={title}
                        contentType={ContentType.Article}
                        articleUrlTitle={article.urlTitle}
                        articleId={article.id}
                    />
                </div>

                {article.header && (
                    <div className="article-header__featured-image u-relative_hidden is-not-loading">
                        <ImageLazyLoad source={article.header}>
                            <p className="article-header__read-time">{IconTime()}{ReadTime} minute read</p>
                        </ImageLazyLoad>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ArticleHeader;
